import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, Printer, MapPin, Package, Settings, Plane } from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { A4PrintLayout, ThermalPrintLayout } from '../../shared/inventory/DeliveryLabelLayouts';
import axios from 'axios';

function PrintDeliveries({ userId, setActiveComponent }) {
  const [selectedDeliveryPoints, setSelectedDeliveryPoints] = useState([]);
  const [selectedProvinceDeliveries, setSelectedProvinceDeliveries] = useState([]);
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [provinceDeliveries, setProvinceDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printerType, setPrinterType] = useState('a4');
  const printRef = useRef();

  useEffect(() => {
    fetchAllDeliveries();
  }, [userId]);

  async function fetchAllDeliveries() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const authConfig = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
      // Fetch regular delivery points
      const userResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/${userId}`, authConfig);
      const allDeliveryPoints = userResponse.data.delivery_points || [];
      
      // Filter out deliveries with provider_id (those are handled by providers)
      const userOwnDeliveries = allDeliveryPoints.filter(dp => !dp.provider_id);
      setDeliveryPoints(userOwnDeliveries);

      // Fetch province deliveries
      const provinceResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/user/${userId}/province-deliveries`
      );
      const allProvinceDeliveries = provinceResponse.data.province_deliveries || [];
      
      // Filter out province deliveries with provider_id
      const userOwnProvinceDeliveries = allProvinceDeliveries.filter(dp => !dp.provider_id);
      setProvinceDeliveries(userOwnProvinceDeliveries);
      
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSelectionChange(dp, isProvince = false) {
    if (isProvince) {
      setSelectedProvinceDeliveries(prev => {
        if (prev.some(item => item.id === dp.id)) {
          return prev.filter(item => item.id !== dp.id);
        } else {
          return [...prev, dp];
        }
      });
    } else {
      setSelectedDeliveryPoints(prev => {
        if (prev.some(item => item.id === dp.id)) {
          return prev.filter(item => item.id !== dp.id);
        } else {
          return [...prev, dp];
        }
      });
    }
  }

  function handleSelectAll(isProvince = false) {
    if (isProvince) {
      if (selectedProvinceDeliveries.length === provinceDeliveries.length) {
        setSelectedProvinceDeliveries([]);
      } else {
        setSelectedProvinceDeliveries([...provinceDeliveries]);
      }
    } else {
      if (selectedDeliveryPoints.length === deliveryPoints.length) {
        setSelectedDeliveryPoints([]);
      } else {
        setSelectedDeliveryPoints([...deliveryPoints]);
      }
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: printerType === 'thermal' 
      ? `
        @page {
          size: 80mm auto;
          margin: 0;
        }
        @media print {
          body { margin: 0; }
        }
      `
      : `
        @page {
          size: A4 portrait;
          margin: 10mm;
        }
        @media print {
          body { margin: 0; }
        }
      `
  });

  const allLimaSelected = deliveryPoints.length > 0 && selectedDeliveryPoints.length === deliveryPoints.length;
  const allProvinciaSelected = provinceDeliveries.length > 0 && selectedProvinceDeliveries.length === provinceDeliveries.length;
  const totalSelected = selectedDeliveryPoints.length + selectedProvinceDeliveries.length;

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setActiveComponent('Analytics')}
              className="flex items-center gap-2 text-slate-700 hover:text-brand-primary transition-colors duration-200 font-montserrat"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm sm:text-base">Volver</span>
            </button>
            <h1 className="text-lg sm:text-xl font-extrabold text-brand-primary font-montserrat">
              Rotulado
            </h1>
            <div className="w-20"></div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          
          {/* Instructions Card */}
          <div className="bg-gradient-to-r from-brand-primary/10 to-brand-gold/10 border-l-4 border-brand-primary rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <Package className="w-5 h-5 text-brand-primary flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-base font-bold text-brand-primary font-montserrat mb-1">
                  Selecciona las entregas a imprimir
                </h2>
                <p className="text-sm text-slate-700 font-montserrat">
                  Escoge las entregas que deseas imprimir en formato de etiquetas
                </p>
              </div>
            </div>
          </div>

          {/* Printer Type Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <div className="flex items-center gap-3 mb-3">
              <Settings className="w-5 h-5 text-brand-primary" />
              <h3 className="text-base font-bold text-slate-900 font-montserrat">
                Tipo de Impresora
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setPrinterType('a4')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'a4'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'a4' ? 'border-brand-primary' : 'border-slate-300'
                  }`}>
                    {printerType === 'a4' && (
                      <div className="w-3 h-3 rounded-full bg-brand-primary"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora A4
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Papel estándar A4 (210 × 297 mm)
                  <br />
                  8 etiquetas por hoja
                </p>
              </button>

              <button
                onClick={() => setPrinterType('thermal')}
                className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                  printerType === 'thermal'
                    ? 'border-brand-primary bg-brand-primary/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    printerType === 'thermal' ? 'border-brand-primary' : 'border-slate-300'
                  }`}>
                    {printerType === 'thermal' && (
                      <div className="w-3 h-3 rounded-full bg-brand-primary"></div>
                    )}
                  </div>
                  <span className="font-bold text-slate-900 font-montserrat">
                    Impresora Térmica
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-montserrat ml-7">
                  Xprinter XP-365B (80mm ancho)
                  <br />
                  1 etiqueta por rollo
                </p>
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {/* Empty State */}
          {!loading && deliveryPoints.length === 0 && provinceDeliveries.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
              <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
                No hay entregas disponibles
              </h3>
              <p className="text-sm text-slate-600 font-montserrat">
                Crea una entrega para poder imprimir etiquetas
              </p>
            </div>
          )}

          {/* Lima Delivery Points Section */}
          {!loading && deliveryPoints.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-brand-primary" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                    Entregas Lima ({deliveryPoints.length})
                  </h3>
                </div>
                <button
                  onClick={() => handleSelectAll(false)}
                  className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors duration-200 font-montserrat text-sm"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                    allLimaSelected ? 'bg-brand-primary border-brand-primary' : 'border-slate-300'
                  }`}>
                    {allLimaSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-slate-700 font-medium">
                    {allLimaSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </span>
                </button>
              </div>

              <div className="space-y-3">
                {deliveryPoints.map(dp => {
                  const isSelected = selectedDeliveryPoints.some(item => item.id === dp.id);
                  return (
                    <button
                      key={dp.id}
                      onClick={() => handleSelectionChange(dp, false)}
                      className={`w-full bg-white rounded-xl border-2 transition-all duration-200 p-4 text-left ${
                        isSelected 
                          ? 'border-brand-primary shadow-md' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                          isSelected ? 'bg-brand-primary border-brand-primary' : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>

                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-brand-primary' : 'bg-slate-100'
                        }`}>
                          <MapPin className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-base font-bold text-slate-900 font-montserrat truncate">
                              {dp.recipient_name}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 font-montserrat whitespace-nowrap">
                              ID: {dp.id}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 font-montserrat line-clamp-1 mb-1">
                            {dp.address}
                          </p>
                          {dp.district && (
                            <p className="text-xs text-slate-500 font-montserrat">
                              {dp.district}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Province Delivery Points Section */}
          {!loading && provinceDeliveries.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Plane className="w-5 h-5 text-brand-gold" />
                  <h3 className="text-lg font-bold text-slate-900 font-montserrat">
                    Entregas Provincia ({provinceDeliveries.length})
                  </h3>
                </div>
                <button
                  onClick={() => handleSelectAll(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg transition-colors duration-200 font-montserrat text-sm"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors duration-200 ${
                    allProvinciaSelected ? 'bg-brand-gold border-brand-gold' : 'border-slate-300'
                  }`}>
                    {allProvinciaSelected && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-slate-700 font-medium">
                    {allProvinciaSelected ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  </span>
                </button>
              </div>

              <div className="space-y-3">
                {provinceDeliveries.map(dp => {
                  const isSelected = selectedProvinceDeliveries.some(item => item.id === dp.id);
                  return (
                    <button
                      key={dp.id}
                      onClick={() => handleSelectionChange(dp, true)}
                      className={`w-full bg-white rounded-xl border-2 transition-all duration-200 p-4 text-left ${
                        isSelected 
                          ? 'border-brand-gold shadow-md' 
                          : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                          isSelected ? 'bg-brand-gold border-brand-gold' : 'border-slate-300'
                        }`}>
                          {isSelected && (
                            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>

                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-brand-gold' : 'bg-slate-100'
                        }`}>
                          <Plane className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-slate-500'}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <h3 className="text-base font-bold text-slate-900 font-montserrat truncate">
                              {dp.recipient_name}
                            </h3>
                            <span className="text-xs font-semibold text-slate-500 font-montserrat whitespace-nowrap">
                              ID: {dp.id}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600 font-montserrat line-clamp-1 mb-1">
                            {dp.province} - {dp.district}
                          </p>
                          <p className="text-xs text-slate-500 font-montserrat line-clamp-1">
                            {dp.address}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Fixed Footer - Print Button */}
      <div className="flex-shrink-0 bg-white border-t border-slate-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <button
            onClick={handlePrint}
            disabled={totalSelected === 0}
            className="w-full bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed font-montserrat flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            <span>
              Imprimir {totalSelected > 0 && `(${totalSelected})`}
            </span>
          </button>
        </div>
      </div>

      {/* Hidden Print Content */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {printerType === 'a4' ? (
            <A4PrintLayout 
              limaDeliveries={selectedDeliveryPoints}
              provinciaDeliveries={selectedProvinceDeliveries}
            />
          ) : (
            <ThermalPrintLayout
              limaDeliveries={selectedDeliveryPoints}
              provinciaDeliveries={selectedProvinceDeliveries}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default PrintDeliveries;
